<template>
  <OrderTableEntry
    class="order-table-bundle"
    is-bundle
  >
    <template #heading>
      <OrderTableItemTitle
        :text="item.name"
        :url="`/geraete/${item.slug}`"
      />
    </template>
    <template #information>
      <div class="order-table-bundle__items">
        <BundleList
          class="order-table-bundle__list"
          :items="bundleListItems"
        >
          <!-- eslint-disable-next-line vue/no-template-shadow -->
          <template #image="{ item }">
            <CdnImage
              :alt="item.teaserImage.alt"
              :src="item.teaserImage.src"
            />
          </template>
        </BundleList>
        <div class="order-table-bundle__availabilty">
          <span
            v-if="availableState"
            :class="availableModifier"
            class="order-table-bundle__status text--bold"
          >
            {{ availableState }}
          </span>
          <span
            v-if="availability.deliveryTime"
            class="order-table-bundle__delivery"
          >
            Lieferzeit: {{ availability.deliveryTime }}
          </span>
        </div>
        <OrderTableItemProvider
          :provider-key="item.provider"
          class="order-table-bundle__info"
        />
        <div
          v-if="couponRedeemed && redeemedCouponType === 'voucher'"
          class="order-table-bundle__voucher-status"
          :class="[
            item.voucher
              ? 'order-table-bundle__voucher-status--success'
              : 'order-table-bundle__voucher-status--not-applicable',
          ]"
        >
          {{ item.voucher ? voucherMessage.success : voucherMessage.notApplicable }}
        </div>
      </div>
    </template>
    <template #monthly>
      <div
        v-if="item.monthlyPrice"
        class="order-table-bundle__entry"
        data-name="Monatlicher Preis:"
      >
        <div class="order-table-bundle__price">
          <OrderTableItemPrice :price="item.monthlyPrice" />
        </div>
      </div>
    </template>
    <template #onetime>
      <div
        class="order-table-bundle__entry"
        data-name="Einmaliger Preis:"
      >
        <div class="order-table-bundle__price">
          <OrderTableItemPrice
            v-if="displayWasPrice"
            :price="item.wasPrice"
            struck-through
          />
          <OrderTableItemPrice
            v-else-if="displayOriginalPrice"
            :price="item.originalPrice"
            struck-through
          />
          <OrderTableItemPrice
            v-if="hasVoucherReduction"
            :price="item.originalPrice"
          />
          <OrderTableItemPrice
            v-else
            :price="item.price"
            show-null-price
          />
        </div>
      </div>
    </template>
    <template #amount>
      <div
        class="order-table-bundle__entry order-table-bundle__quantity"
        data-name="Menge"
      >
        <ChangeItemInCartButton :item="item" />
      </div>
    </template>
    <template #sum>
      <div
        class="order-table-bundle__entry"
        data-name="Einmaliger Gesamtpreis:"
      >
        <div class="order-table-bundle__price">
          <OrderTableItemPrice
            v-if="displayTotalWasPrice"
            :price="totalWasPrice"
            struck-through
          />
          <OrderTableItemPrice
            v-if="displayTotalOriginalPrice"
            :price="totalOriginalPrice"
            struck-through
          />
          <OrderTableItemPrice
            v-if="hasVoucherReduction"
            :price="totalOriginalPrice"
            class="text--primary"
          />
          <OrderTableItemPrice
            v-else
            :price="totalPrice"
            class="text--primary"
            show-null-price
          />
        </div>
      </div>
    </template>
  </OrderTableEntry>
</template>

<script>
import { mapGetters } from 'vuex';
import { BundleList, availability } from '@i22-td-smarthome/component-library';
import { processMediaItem } from '@i22/rocket';

import RetrievePrice from '@/mixins/retrieve-price';
import CdnImage from '@/components/cdn-image.vue';
import OrderTableEntry from '@/components/order-table-entry.vue';
import ChangeItemInCartButton from '@/components/change-item-in-cart-button.vue';
import OrderTableItemPrice from '@/components/order-table-item-price.vue';
import OrderTableItemProvider from '@/components/order-table-item-provider.vue';
import OrderTableItemTitle from '@/components/order-table-item-title.vue';

export default {
  name: 'OrderTableBundle',
  components: {
    OrderTableEntry,
    ChangeItemInCartButton,
    OrderTableItemPrice,
    OrderTableItemProvider,
    OrderTableItemTitle,
    CdnImage,
    BundleList,
  },
  mixins: [RetrievePrice],
  props: {
    item: {
      type: Object,
      required: true,
    },
    bundleItems: {
      type: Array,
      required: true,
    },
    voucherMessage: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('cart', ['couponRedeemed', 'redeemedCouponType']),
    availability() {
      return availability(this.item.stock, this.item.deliveryLeadTime);
    },
    availableState() {
      if (this.item.category === 'Tariff') {
        return null;
      }
      return this.availability.text;
    },
    availableModifier() {
      return `order-table-bundle__status--${this.availability.modifier}`;
    },
    hasVoucherReduction() {
      // Reductions due to a voucher shall not be displayed on the order-table-item
      return this.item.voucher && this.item.reductionAmount > 0;
    },
    totalOriginalPrice() {
      if (this.item.originalPrice) {
        return this.item.originalPrice * this.item.quantity;
      }
      return null;
    },
    totalWasPrice() {
      if (this.item.wasPrice) {
        return this.item.wasPrice * this.item.quantity;
      }
      return null;
    },
    totalPrice() {
      if (this.item.price) {
        return this.item.price * this.item.quantity;
      }
      return null;
    },
    link() {
      // contracts do not have special pages
      if (this.item.contractNumber || !this.item.slug) {
        return null;
      }
      return `/geraete/${this.item.slug}`;
    },
    displayWasPrice() {
      return this.item.wasPrice && this.item.price < this.item.wasPrice;
    },
    displayOriginalPrice() {
      return (
        !this.hasVoucherReduction &&
        this.item.originalPrice &&
        this.item.price < this.item.originalPrice
      );
    },
    displayTotalOriginalPrice() {
      return (
        !this.hasVoucherReduction &&
        this.item.originalPrice &&
        this.totalPrice < this.totalOriginalPrice
      );
    },
    displayTotalWasPrice() {
      return this.item.wasPrice && this.totalPrice < this.totalWasPrice;
    },
    bundleListItems() {
      return this.bundleItems.map((item) => ({
        teaserImage: {
          src: processMediaItem(item.teaserImage)?.medium,
          alt: item.name,
        },
        name: `${item.quantity}x ${item.name}`,
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.order-table-bundle {
  $grid-column-width: calc(1 / 12) * 100%;
  $offset-xs: calc(25% + $grid-base);
  $offset-sm: 150px + $grid-base;
  $offset-md: calc(17% + $grid-base);
  $offset-xl: 68px + $grid-base;

  &__items {
    > * {
      margin-left: $offset-xs;

      @include mq($mq-sm) {
        margin-left: $offset-sm;
      }

      @include mq($mq-md) {
        margin-left: $offset-md;
      }

      @include mq($mq-xl) {
        margin-left: $offset-xl;
      }
    }
  }

  &__list {
    margin-left: 0;

    :deep(.bundle-list__product-image) {
      margin: 0;
      flex: 0 0 calc(25% + $grid-base);
      height: 50px;
      text-align: center;

      @include mq($mq-md) {
        flex: 0 0 $offset-md;
        max-width: none;
      }
    }
  }

  // Status
  &__status {
    &--in-stock {
      color: var(--color-success);
    }

    &--limited-availability {
      color: var(--color-warning);
    }

    &--out-of-stock {
      color: var(--color-error);
    }
  }

  &__voucher-status {
    @include font-size(15px, 16px);
    margin-top: $grid-base * 0.5;

    &--success {
      color: var(--color-success);
    }
    &--not-applicable {
      color: var(--color-error);
    }
  }

  &__availabilty {
    @include font-size(15px, 24px);
    display: flex;
    flex-wrap: wrap;
    grid-gap: 0 $grid-base * 0.5;
    @include mq(false, $mq-sm - 1) {
      flex-direction: column;
    }
  }

  &__delivery {
    &::before {
      @include mq($mq-sm) {
        content: '|';
        display: inline-block;
        padding-right: $grid-base * 0.5;
      }
    }
  }

  &__entry {
    @include mq(false, $mq-md - 1) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      &:before {
        content: attr(data-name);
        display: block;
        @include font-size(15px, 16px);
      }
    }
  }

  &__quantity {
    @include mq(false, $mq-md - 1) {
      align-items: flex-start;
    }
  }

  &__price {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
  }

  :deep(.change-item-in-cart-button) {
    justify-content: center;
  }
}
</style>
